class TextRevealAnimations {
  constructor() {
    this.elements = [];
    this.observer = null;
    this.init();
  }

  init() {
    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    this.setupElements();
    this.setupObserver();
    this.bindEvents();
  }

  setupElements() {
    const textRevealElements = document.querySelectorAll('.text-reveal');
    
    textRevealElements.forEach(element => {
      this.processElement(element);
    });
  }

  processElement(element) {
    const isWords = element.classList.contains('text-reveal-words');
    const isLetters = element.classList.contains('text-reveal-letters');
    const isLines = element.classList.contains('text-reveal-lines');

    if (isWords) {
      this.wrapWords(element);
    } else if (isLetters) {
      this.wrapLetters(element);
    } else if (isLines) {
      this.wrapLines(element);
    }

    this.elements.push(element);
  }

  wrapWords(element) {
    const text = element.textContent;
    const words = text.split(/(\s+)/);
    
    element.innerHTML = words.map(word => {
      if (word.trim() === '') return word;
      return `<span class="reveal-word">${word}</span>`;
    }).join('');
  }

  wrapLetters(element) {
    const text = element.textContent;
    
    element.innerHTML = text.split('').map(char => {
      if (char === ' ') return ' ';
      return `<span class="reveal-letter">${char}</span>`;
    }).join('');
  }

  wrapLines(element) {
    const text = element.innerHTML;
    const lines = text.split('<br>');
    
    element.innerHTML = lines.map(line => {
      if (line.trim() === '') return line;
      return `<span class="reveal-line">${line}</span>`;
    }).join('<br>');
  }

  setupObserver() {
    const options = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.animateElement(entry.target);
        }
      });
    }, options);

    this.elements.forEach(element => {
      this.observer.observe(element);
    });
  }

  animateElement(element) {
    element.classList.add('animating');
    
    const isWords = element.classList.contains('text-reveal-words');
    const isLetters = element.classList.contains('text-reveal-letters');
    const isLines = element.classList.contains('text-reveal-lines');

    let delay = 0;
    let increment = 0;

    if (isWords) {
      increment = 80; // 0.08s between words
      const words = element.querySelectorAll('.reveal-word');
      this.staggerElements(words, delay, increment);
    } else if (isLetters) {
      increment = 30; // 0.03s between letters
      const letters = element.querySelectorAll('.reveal-letter');
      this.staggerElements(letters, delay, increment);
    } else if (isLines) {
      increment = 150; // 0.15s between lines
      const lines = element.querySelectorAll('.reveal-line');
      this.staggerElements(lines, delay, increment);
    }

    // Mark as animated after stagger completes
    setTimeout(() => {
      element.classList.add('animated');
      element.classList.remove('animating');
    }, delay + (increment * 2));
  }

  staggerElements(elements, startDelay, increment) {
    elements.forEach((el, index) => {
      setTimeout(() => {
        el.style.transitionDelay = `${index * increment}ms`;
        el.style.transform = 'translateY(0)';
        el.style.opacity = '1';
      }, startDelay + (index * increment));
    });
  }

  // Manual trigger method
  triggerAnimation(selector) {
    const element = document.querySelector(selector);
    if (element && this.elements.includes(element)) {
      this.animateElement(element);
    }
  }

  // Reset animation method
  resetAnimation(selector) {
    const element = document.querySelector(selector);
    if (element) {
      element.classList.remove('animated', 'animating');
      const reveals = element.querySelectorAll('.reveal-word, .reveal-letter, .reveal-line');
      reveals.forEach(el => {
        el.style.transitionDelay = '';
        el.style.transform = '';
        el.style.opacity = '';
      });
    }
  }

  // Refresh method for dynamic content
  refresh() {
    this.elements = [];
    if (this.observer) {
      this.observer.disconnect();
    }
    this.setupElements();
    this.setupObserver();
  }

  bindEvents() {
    // Listen for section loading events (Shopify theme sections)
    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => this.refresh(), 100);
    });

    // Listen for custom refresh events
    document.addEventListener('textReveal:refresh', () => {
      this.refresh();
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.elements = [];
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.textRevealAnimations = new TextRevealAnimations();
  });
} else {
  window.textRevealAnimations = new TextRevealAnimations();
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = TextRevealAnimations;
}